import { useNavigate } from 'react-router';
import { MeasureIntervalState } from '../../types/sharedTypeImpl.ts';
import { formatBatchTimeRemaining, formatCycleTime, formatDuration } from '../../utils/fieldFormats.ts';
import { openLinkInNewTab } from '../../utils/navigation.ts';
import { getBatchTimeRemainingStyle } from '../../utils/styles.ts';
import { DelayStatusIndicator } from './delayStatusIndicator.tsx';


interface Props {
    data: MeasureIntervalState[],
    onPartCountClicked: (row: MeasureIntervalState) => void,
    onMeasureClicked: (row: MeasureIntervalState) => void,
    onEndSetupClicked: (row: MeasureIntervalState) => void
}

export function MeasureIntervalTable({
    data,
    onPartCountClicked,
    onMeasureClicked,
    onEndSetupClicked,
}: Props) {

    const cycleTimeHelp = 'Latest stable cycle time (5 parts produced in a row with the same time)';

    function highlightIssue(row: MeasureIntervalState): boolean {
        return row.stopStatus === 'workCounter' || row.stopStatus === 'toolChange' || row.stopStatus === 'helpMe';
    }

    return (
        <table>
            <thead>
                <tr>
                    <th>Equipment name</th>
                    <th>Part number</th>
                    <th>Order number</th>
                    <th>Parts count</th>
                    <th>Measure interval</th>
                    <th title={cycleTimeHelp}>Cycle time</th>
                    <th title='Predicted batch end based on the part count, target quantity and production rate.&#10;Default value is calculated from just the cycle time.&#10;The value in parentheses also considers the efficiency.'>
                        Batch time
                    </th>
                    <th>Interval time</th>
                    <th>Last measured</th>
                    <th>Status</th>
                    <th />
                </tr>
            </thead>
            <tbody>
                {data?.map(row =>
                    <tr key={row.equipment} className={highlightIssue(row) ? 'highlight--issue' : ''}>
                        <EquipmentName row={row} />
                        <ProductName row={row} />
                        <OrderNumber row={row} />
                        <PartsCount row={row} onPartCountClicked={onPartCountClicked} />
                        <MeasureInterval row={row} />
                        <CycleTime row={row} />
                        <BatchTime row={row} />
                        <IntervalTime row={row} />
                        <LastMeasured row={row} />
                        <Status row={row} onEndSetupClicked={onEndSetupClicked} />
                        <MeasureButton row={row} onMeasureClicked={onMeasureClicked} />
                    </tr>
                )}
            </tbody>
        </table>
    );
}


function EquipmentName({ row }: { row: MeasureIntervalState }) {
    const navigate = useNavigate();

    const [coordiates, name] = row.displayName.split(': ');

    return (
        <td className='textAlignLeft link-button--non-intrusive'>
            <span onClick={() => navigate(`/machine-log#equipment=${row.equipment}`)}>
                {coordiates}: <span style={{ textDecoration: 'underline' }}>{name}</span>
            </span>
        </td>
    );
}

function ProductName({ row }: { row: MeasureIntervalState }) {
    return (
        <td className='textAlignLeft' title={row.product ?? undefined}>
            {row.partDisplayName}
        </td>
    );
}

function OrderNumber({ row }: { row: MeasureIntervalState }) {
    return (
        <td className='textAlignLeft'>
            {row.orderNumber}
        </td>
    );
}

function PartsCount({
    row,
    onPartCountClicked
}: {
    row: MeasureIntervalState,
    onPartCountClicked: (row: MeasureIntervalState) => void
}) {
    const showcurrentPartCount = row.partCount != null;
    const showTargetQuantity = showcurrentPartCount && row.targetQuantity != null;
    const isTargetReached = showTargetQuantity && (row.partCount ?? 0) >= (row.targetQuantity ?? 0);

    const tdClassName = showcurrentPartCount ? 'editable' : '';
    const currentPartCountStyle = isTargetReached ? { color: 'red' } : {};
    return (
        <td className={tdClassName} onClick={e => onPartCountClicked(row)}>
            {showcurrentPartCount && <span style={currentPartCountStyle}>{row.partCount}</span>}
            {showTargetQuantity && <span> / {row.targetQuantity}</span>}
        </td>
    );
}

function MeasureInterval({ row }: { row: MeasureIntervalState }) {
    const showMeasureInterval = row.intervalCount != null && row.intervalTarget != null;

    return (
        <td>{showMeasureInterval && `${row.intervalCount} / ${row.intervalTarget}`}</td>
    );
}

function CycleTime({ row }: { row: MeasureIntervalState }) {
    return (
        <td>{row.cycleTime != null && formatCycleTime(row.cycleTime)}</td>
    );
}

function BatchTime({ row }: { row: MeasureIntervalState }) {
    const optimisticMillies = row.batchEndOptimistic ? +row.batchEndOptimistic - Date.now() : null;
    const realisticMillies = row.batchEndRealistic ? +row.batchEndRealistic - Date.now() : null;
    const text = formatBatchTimeRemaining(optimisticMillies, realisticMillies);
    const cellStyle = getBatchTimeRemainingStyle(optimisticMillies, row.partCount, row.targetQuantity, row.inOfflineMode);

    return (
        <td style={cellStyle}>
            {optimisticMillies != null && text}
        </td>
    );
}

function IntervalTime({ row }: { row: MeasureIntervalState }) {
    if (row.intervalDueAt == null) {
        return <td />;
    }

    const timeDiff = +row.intervalDueAt - +new Date();
    const timeRemaining = row.isIntervalUp === false ? Math.max(0, timeDiff) : timeDiff;
    const timeRemainingString = formatDuration(timeRemaining, true, false);

    let intervalTimeStyle: React.CSSProperties = {};
    if (row.isIntervalUp) {
        intervalTimeStyle = { color: 'red' };
    } else if (timeRemaining != null && timeRemaining <= 15 * 60 * 1000) {
        intervalTimeStyle = { color: 'orange' };
    }

    const showDelayStatus = timeRemaining < 15 * 60_000;
    const showNumberOfParts = row.numberOfParts != null && row.numberOfParts > 1;
    const numberOfPartsStyle = showNumberOfParts && !showDelayStatus ? { marginLeft: '8px' } : {};

    return (
        <td style={intervalTimeStyle}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '0 8px' }}>
                {timeRemainingString}
                {showDelayStatus && <DelayStatusIndicator target={row.intervalDueAt} />}
                {showNumberOfParts && <span style={numberOfPartsStyle}>{row.numberOfParts}pcs</span>}
            </div>
        </td>
    );
}

function LastMeasured({ row }: { row: MeasureIntervalState }) {
    return (
        <td>{
            row.lastMeasureTime?.toLocaleTimeString([], {
                weekday: 'short', hour: '2-digit', hour12: false, minute: '2-digit'
            })
        }</td>
    );
}

function Status({
    row,
    onEndSetupClicked,
}: {
    row: MeasureIntervalState,
    onEndSetupClicked: (row: MeasureIntervalState) => void,
}) {
    const statusParts = [];
    let showEndSetupButton = false;
    if (row.isActive) {
        if (row.inOfflineMode) {
            statusParts.push('MT-LINKi not available');
        } else if (row.isSetupComplete == false) {
            statusParts.push('In setup');
            showEndSetupButton = true;
        } else {
            if (row.stopStatus) {
                const mainText = row.stopStatus === 'workCounter' ? 'Work counter'
                    : row.stopStatus === 'toolChange' ? 'Tool change'
                        : row.stopStatus === 'helpMe' ? 'Help me!'
                            : 'Fix in progress';
                const durationText = row.stopStatusStart
                    ? `(${formatDuration(+new Date() - +row.stopStatusStart, false, true)})`
                    : '';
                statusParts.push(`${mainText} ${durationText}`);
            }
            if (row.projectionType === 'setup-confirmation') {
                statusParts.push('Confirmation measurement');
            }
            if (row.product && (!row.intervalTarget || row.intervalTarget <= 1)) {
                statusParts.push('Interval is not set');
            }
        }
        if (row.activePartCollect) {
            statusParts.push('Collected');
        }
    }
    const statusText = statusParts.join('\n');

    return (
        <td>
            <span style={{ whiteSpace: 'pre-line' }}>{statusText}</span>
            {showEndSetupButton &&
                <button
                    style={{ marginLeft: '16px' }}
                    onClick={() => onEndSetupClicked(row)}
                    children='End setup'
                />
            }
        </td>
    );
}

function MeasureButton({
    row,
    onMeasureClicked
}: {
    row: MeasureIntervalState,
    onMeasureClicked: (row: MeasureIntervalState) => void
}) {

    return (
        <td>
            {row.ongoingRecordId == null
                ? <button
                    onClick={() => onMeasureClicked(row)}
                    children='Measure'
                />
                : <button
                    className='measurement-continue'
                    onClick={() => openLinkInNewTab(`record-view`, { id: row.ongoingRecordId, mode: 'edit' })}
                    children='Continue'
                />
            }
        </td>
    );
}
